import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button, Typography, Paper } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Close } from "@material-ui/icons";
import { setOnboardingFlag } from "../../utils/onboardingFlags";

const useStyles = makeStyles((theme) => ({
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9998,
    pointerEvents: "none",
  },
  backdrop: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 9998,
    pointerEvents: "all",
  },
  spotlight: {
    position: "fixed",
    zIndex: 9999,
    borderRadius: 6,
    boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)",
    pointerEvents: "none",
    transition: "all 0.3s ease",
  },
  tooltip: {
    position: "fixed",
    zIndex: 10000,
    width: 300,
    borderRadius: 12,
    padding: theme.spacing(2.5),
    boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
    transition: "all 0.25s ease",
    pointerEvents: "all",
  },
  stepIndicator: {
    fontSize: "0.72rem",
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
  },
  btnNext: {
    borderRadius: 8,
    fontWeight: 600,
    fontSize: "0.82rem",
    textTransform: "none",
    padding: "6px 18px",
  },
  btnBack: {
    fontSize: "0.8rem",
    textTransform: "none",
    color: theme.palette.text.secondary,
  },
  btnSkip: {
    fontSize: "0.75rem",
    textTransform: "none",
    color: theme.palette.text.disabled,
    marginRight: "auto",
    paddingLeft: 0,
    minWidth: 0,
  },
  closeBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    cursor: "pointer",
    color: theme.palette.text.disabled,
    display: "flex",
    padding: 4,
    borderRadius: "50%",
    "&:hover": { color: theme.palette.text.secondary },
  },
}));

const PADDING = 8;
const TOOLTIP_OFFSET = 14;

function getSpotlightRect(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  // Element is hidden (e.g., inside closed mobile drawer) — treat as not found
  if (r.width === 0 && r.height === 0) return null;
  return {
    top: r.top - PADDING,
    left: r.left - PADDING,
    width: r.width + PADDING * 2,
    height: r.height + PADDING * 2,
  };
}

function computeTooltipPos(spotlight, placement, tooltipH = 180) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const tooltipW = Math.min(300, vw - 32);
  const isMobile = vw < 600;

  // On mobile always anchor to bottom-center to avoid overlapping full-width sidebar
  if (!spotlight || isMobile) {
    if (isMobile && spotlight) {
      // Below the spotlight if space allows, otherwise above
      const { top, height } = spotlight;
      const belowTop = top + height + TOOLTIP_OFFSET;
      const aboveTop = top - tooltipH - TOOLTIP_OFFSET;
      return {
        top: belowTop + tooltipH < vh - 8 ? belowTop : Math.max(8, aboveTop),
        left: vw / 2 - tooltipW / 2,
        width: tooltipW,
      };
    }
    return {
      top: vh / 2 - tooltipH / 2,
      left: vw / 2 - tooltipW / 2,
      width: tooltipW,
    };
  }

  const { top, left, width, height } = spotlight;

  let pos = {};

  if (placement === "right") {
    pos.top = Math.max(8, Math.min(top, vh - tooltipH - 16));
    pos.left = left + width + TOOLTIP_OFFSET;
    if (pos.left + tooltipW > vw - 8) pos.left = Math.max(8, left - tooltipW - TOOLTIP_OFFSET);
  } else if (placement === "bottom") {
    pos.top = top + height + TOOLTIP_OFFSET;
    pos.left = Math.max(8, Math.min(left, vw - tooltipW - 8));
    if (pos.top + tooltipH > vh - 8) pos.top = Math.max(8, top - tooltipH - TOOLTIP_OFFSET);
  } else {
    // center
    pos.top = vh / 2 - tooltipH / 2;
    pos.left = vw / 2 - tooltipW / 2;
  }

  return { ...pos, width: tooltipW };
}

export const TOUR_STORAGE_KEY = "me_guided_tour_done";

// `inDrawer`: passos que apontam para itens do menu lateral. No mobile o
// drawer precisa estar aberto para esses; nos demais (centro e checklist)
// ele deve ficar fechado para não cobrir o conteúdo da página.
const STEPS = [
  {
    target: null,
    placement: "center",
    inDrawer: false,
    title: "🚀 Vamos configurar tudo em 4 passos!",
    content: "A ordem importa! Siga o guia abaixo para garantir que seu atendimento funcione corretamente desde o início.",
  },
  {
    target: "[data-tour='menu-queues']",
    placement: "right",
    inDrawer: true,
    title: "🗂️ Passo 1 — Suas Filas",
    content: "Já criamos as filas Suporte e Vendas pra você começar. Aqui você pode personalizá-las ou criar novas. Toda conexão precisa de ao menos uma fila vinculada para rotear os atendimentos.",
  },
  {
    target: "[data-tour='menu-connections']",
    placement: "right",
    inDrawer: true,
    title: "📱 Passo 2 — Conecte o WhatsApp",
    content: "Escaneie o QR Code para conectar. Na mesma tela, vincule a fila que você criou à conexão — isso é essencial para o roteamento funcionar.",
  },
  {
    target: "[data-tour='menu-users']",
    placement: "right",
    inDrawer: true,
    title: "👥 Passo 3 — Adicione Atendentes",
    content: "Cadastre sua equipe e associe cada atendente à fila correta. Assim os tickets serão distribuídos automaticamente.",
  },
  {
    target: "[data-tour='menu-tickets']",
    placement: "right",
    inDrawer: true,
    title: "💬 Passo 4 — Pronto para Atender!",
    content: "Aqui chegam todas as conversas do seu time. Com a fila vinculada, cada mensagem recebida gerará um ticket atribuído ao departamento certo.",
  },
  {
    target: "[data-tour='onboarding-checklist']",
    placement: "bottom",
    inDrawer: false,
    title: "✅ Seu guia de progresso",
    content: "Este checklist acompanha sua configuração. Siga a ordem dos passos para garantir que tudo funcione corretamente. Bora! 🎉",
  },
];

// Encontra o elemento-alvo VISÍVEL (ignora duplicatas ocultas, ex.: o mesmo
// data-tour renderizado em mais de um ramo do menu por perfil/condição).
function findVisibleTarget(selector) {
  const els = Array.from(document.querySelectorAll(selector));
  return (
    els.find((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    }) ||
    els[0] ||
    null
  );
}

const GuidedTour = ({ run, onFinish }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [step, setStep] = useState(0);
  const [spotlight, setSpotlight] = useState(null);

  const current = STEPS[step];
  const total = STEPS.length;

  const updateSpotlight = useCallback(() => {
    if (!current.target) { setSpotlight(null); return; }
    const el = findVisibleTarget(current.target);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });

    // Acompanha o alvo até a posição estabilizar. Importante porque o menu
    // lateral abre com animação (no mobile) e o scroll é suave: medir uma
    // única vez pode "travar" o foco numa posição intermediária da animação,
    // fazendo o destaque ficar fora do item (ex.: menu Departamentos).
    let elapsed = 0;
    let lastKey = "";
    let stable = 0;
    const tick = () => {
      const target = findVisibleTarget(current.target);
      const rect = target ? getSpotlightRect(target) : null;
      if (rect) {
        setSpotlight(rect);
        const key = `${Math.round(rect.top)}:${Math.round(rect.left)}:${Math.round(rect.width)}`;
        stable = key === lastKey ? stable + 1 : 0;
        lastKey = key;
        // Mesma posição 2x seguidas => animação/scroll terminaram.
        if (stable >= 2) return;
      }
      elapsed += 90;
      if (elapsed <= 1200) {
        setTimeout(tick, 90);
      } else if (!rect) {
        setSpotlight(null);
      }
    };
    setTimeout(tick, 120);
  }, [current.target]);

  useEffect(() => {
    if (!run) { setStep(0); return; }
    // Informa ao layout se o passo atual precisa do menu lateral aberto (mobile).
    window.dispatchEvent(
      new CustomEvent("guidedtour:step", {
        detail: { needsDrawer: !!current.inDrawer },
      })
    );
    updateSpotlight();
  }, [run, step, current.inDrawer, updateSpotlight]);

  // Mantém o foco alinhado ao alvo durante scroll, resize e rotação de tela
  // (evita o destaque "sair da posição" no mobile).
  useEffect(() => {
    if (!run || !current.target) return;
    const remeasure = () => {
      const el = findVisibleTarget(current.target);
      const rect = el ? getSpotlightRect(el) : null;
      if (rect) setSpotlight(rect);
    };
    window.addEventListener("resize", remeasure);
    window.addEventListener("orientationchange", remeasure);
    window.addEventListener("scroll", remeasure, true);
    return () => {
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("orientationchange", remeasure);
      window.removeEventListener("scroll", remeasure, true);
    };
  }, [run, current.target]);

  const finish = useCallback(() => {
    setOnboardingFlag(TOUR_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent("guidedtour:finish"));
    if (onFinish) onFinish();
  }, [onFinish]);

  if (!run) return null;

  const tooltipPos = computeTooltipPos(spotlight, current.placement);
  const isLast = step === total - 1;
  const isFirst = step === 0;

  return createPortal(
    <>
      {/* Camada de bloqueio de cliques. Fica escura quando não há spotlight
          (passo central ou enquanto mede o alvo) e transparente quando o
          spotlight já escurece o redor via boxShadow — assim o item
          destacado aparece nítido, sem dupla camada escura. */}
      <div
        className={classes.backdrop}
        style={spotlight ? { backgroundColor: "transparent" } : undefined}
        onClick={() => {}}
      />

      {/* Spotlight */}
      {spotlight && (
        <div
          className={classes.spotlight}
          style={{
            top: spotlight.top,
            left: spotlight.left,
            width: spotlight.width,
            height: spotlight.height,
          }}
        />
      )}

      {/* Tooltip */}
      <Paper
        className={classes.tooltip}
        style={{ top: tooltipPos.top, left: tooltipPos.left, width: tooltipPos.width }}
        elevation={6}
      >
        <div
          className={classes.closeBtn}
          onClick={finish}
        >
          <Close style={{ fontSize: 16 }} />
        </div>

        <Typography className={classes.stepIndicator}>
          {step + 1} de {total}
        </Typography>

        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 6 }}>
          {current.title}
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ lineHeight: 1.5 }}>
          {current.content}
        </Typography>

        <div className={classes.actions}>
          <Button className={classes.btnSkip} onClick={finish}>
            Pular
          </Button>
          {!isFirst && (
            <Button className={classes.btnBack} onClick={() => setStep(s => s - 1)}>
              ← Voltar
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            className={classes.btnNext}
            onClick={() => {
              if (isLast) finish();
              else setStep(s => s + 1);
            }}
          >
            {isLast ? "Concluir 🎉" : "Próximo →"}
          </Button>
        </div>
      </Paper>
    </>,
    document.body
  );
};

export default GuidedTour;
